import type {
  FontAttributes,
  FontHierarchy,
  FontSource,
  FontVariants,
  FontWeightVariants,
  Typography,
} from './brands';

export const fontsConfig = {
  extensionPattern: /\.([0-9a-z]+$)/i,
  weightMapper: {
    bold: 600,
    regular: 400,
  },
} as const;

/**
 * Creates a @font-face src property from a given source paths list
 */
export function getFontSRC(sourceURL: FontSource): string {
  return sourceURL
    .reduce((acc, url) => {
      const [, fontExtension] = url.match(fontsConfig.extensionPattern) || [];

      acc.push(`url("${url}") format("${fontExtension}")`);

      return acc;
    }, [] as string[])
    .sort()
    .reverse()
    .join(', ');
}

/**
 * Gets the correct font weight number from the given font variant
 */
export function getFontWeight(variant: FontWeightVariants) {
  return fontsConfig.weightMapper[variant];
}

/**
 * Creates a list of @font-face variants from a given font source paths list
 */
export function createFontVariants({ fontFamily, fontStyle, sources }: FontAttributes) {
  const fontVariants = Object.keys(sources) as FontVariants;

  return fontVariants.map((variant) => {
    const fontWeight = getFontWeight(variant);
    const src = getFontSRC(sources[variant]);

    return {
      '@font-face': {
        fontFamily,
        fontStyle,
        fontWeight,
        src,
        fontDisplay: 'swap',
      },
    };
  });
}

/**
 * Creates a @font-face list from given brand typography object
 */
export function createFontFace(fontObj: Typography) {
  const fontHierarchy = Object.keys(fontObj) as FontHierarchy;

  return fontHierarchy.flatMap((hierarchy) => createFontVariants(fontObj[hierarchy]));
}
